import { BadInputError } from '@watershed/errors/BadInputError';

import { ASTCall, stringifyExpr } from '../ast';
import { Context, Func, SqlExpression } from '../types';
import { ExprType, stringifyType } from '../expr_types';
import {
  assertArgShape,
  assertKwargShape,
  typecheckExpression,
  TypecheckWarning,
} from '../typecheck';
import { evalExpression } from '../eval';
import { exprToSql } from '../sql';
import { safeMaxStage } from '../utils';
import { mergeErrors } from '../sql/internal';

// Function for logical and. Syntax:
// and(true, false, ...)
// returns true if and only if all of its arguments are true
export const and: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 2);
    assertKwargShape(call, [], false, undefined, undefined);

    const argTypes = call.args.map((arg) =>
      typecheckExpression(ctx, arg, warnings)
    );

    argTypes.forEach((a, i) => {
      if (a.ty !== 'boolean') {
        throw new BadInputError(
          `Arguments of \`and\` must be type \`boolean\`: got type \`${stringifyType(
            a
          )}\` for argument \`${stringifyExpr(call.args[i])}\`).`
        );
      }
    });

    return { ty: 'boolean' };
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const vals = call.args.map((arg) => evalExpression(ctx, arg));
    return vals.every((v) => v === true);
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const argsSql = call.args.map((arg) => exprToSql(ctx, arg));
    return {
      expr: `(${argsSql.map((a) => a.expr).join(' AND ')})`,
      stage: safeMaxStage(argsSql),
      unitExpr: null,
      error: mergeErrors(argsSql.map((a) => a.error)),
      trace: null,
    };
  },
};
